import { useEffect, useState, useCallback, useRef } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { usePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { MoreHorizontal, Pencil, Plus, Search, Trash2, Users, X } from "lucide-react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDate } from "@/lib/format";
import { CreateCustomerDialog } from "@/components/CreateCustomerDialog";
import { EditCustomerDialog } from "@/components/EditCustomerDialog";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { PresenceIndicator } from "@/components/PresenceIndicator";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type CustomerResponse = components["schemas"]["CustomerResponse"];

function addressTooltip(customer: CustomerResponse): string {
  return [
    customer.addressLine1,
    customer.addressLine2,
    customer.city,
    customer.state,
    customer.postalCode,
    customer.country,
  ]
    .filter(Boolean)
    .join("\n");
}

const PAGE_SIZE = 10;

export interface CustomersListFilters {
  name?: string;
  email?: string;
}

export interface CustomersListPageProps {
  onCustomerClick: (id: string) => void;
  /** Initial filters. Updates to filters live in component state. */
  defaultFilters?: CustomersListFilters;
  onFiltersChange?: (filters: CustomersListFilters) => void;
}

export function CustomersListPage({
  onCustomerClick,
  defaultFilters,
  onFiltersChange,
}: CustomersListPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();

  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [nameInput, setNameInput] = useState(defaultFilters?.name ?? "");
  const [emailInput, setEmailInput] = useState(defaultFilters?.email ?? "");
  const debouncedName = useDebouncedValue(nameInput);
  const debouncedEmail = useDebouncedValue(emailInput);

  const [customerToEdit, setCustomerToEdit] = useState<CustomerResponse | null>(null);
  const [customerToDelete, setCustomerToDelete] = useState<CustomerResponse | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canManage = can("manageCustomers");

  // Ref so a fresh inline callback doesn't re-fire the effect (an emit loop via e.g. setSearchParams).
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  const firstEmit = useRef(true);

  useEffect(() => {
    if (firstEmit.current) {
      // Host already passed these values as defaultFilters; nothing to sync.
      firstEmit.current = false;
      return;
    }
    onFiltersChangeRef.current?.({
      name: debouncedName || undefined,
      email: debouncedEmail || undefined,
    });
  }, [debouncedName, debouncedEmail]);

  const fetchCustomers = useCallback(async (cursor?: string) => {
    setLoading(true);
    setError(null);

    try {
      const query: Record<string, string | number> = { Limit: PAGE_SIZE };
      if (debouncedName) query.Name = debouncedName;
      if (debouncedEmail) query.Email = debouncedEmail;
      if (cursor) query.Cursor = cursor;

      const { data, error: apiError } = await api.GET("/v1/customers", {
        params: { query },
      });

      if (apiError || !data) {
        console.error("Failed to load customers:", apiError);
        setError("Failed to load customers");
        return;
      }

      setCustomers(data.data ?? []);
      const next = data.meta?.cursors?.next ?? null;
      setNextCursor(next);
      setHasMore(next !== null);
    } catch (e) {
      console.error("Failed to load customers:", e);
      setError("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [api, debouncedName, debouncedEmail]);

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchCustomers();
  }, [fetchCustomers]);

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchCustomers(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchCustomers(prevCursor);
  }

  async function confirmDelete() {
    const target = customerToDelete;
    if (!target?.id) return;
    const id = target.id;
    setCustomerToDelete(null);
    setDeletingId(id);
    const { error: apiError } = await api.DELETE("/v1/customers/{id}", {
      params: { path: { id } },
    });
    setDeletingId(null);

    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to delete customer"));
      return;
    }

    toast.success("Customer deleted");
    setCustomers((prev) => prev.filter((c) => c.id !== id));
  }

  const currentPage = cursorStack.length + 1;

  const filtered = !!(debouncedName || debouncedEmail);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Receivables"
        title="Customers"
        subtitle="The people and businesses you invoice."
        actions={
          <PermissionGuard allowed={canManage} reason="Only owners and admins can add customers" className="w-full sm:w-auto">
            <Button onClick={() => setCreateOpen(true)} className="w-full gap-1.5 sm:w-auto">
              <Plus className="size-4" />
              New customer
            </Button>
          </PermissionGuard>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-56">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            className="h-9 pl-8"
            placeholder="Search by name"
            aria-label="Filter by name"
          />
        </div>
        <Input
          type="text"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          className="h-9 w-full sm:w-56"
          placeholder="Email"
          aria-label="Filter by email"
        />
        {filtered ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setNameInput("");
              setEmailInput("");
            }}
          >
            <X className="size-4" /> Clear
          </Button>
        ) : null}
      </div>

      {loading ? (
        <LoadingState label="Loading customers..." />
      ) : error ? (
        <ErrorState message="Couldn't load your customers." onRetry={() => fetchCustomers(cursorStack[cursorStack.length - 1])} />
      ) : customers.length === 0 ? (
        filtered ? (
          <EmptyState icon={Search} title="No customers match">
            Try a different name or email, or clear the filters.
          </EmptyState>
        ) : (
          <EmptyState
            icon={Users}
            title="No customers yet"
            action={
              <PermissionGuard allowed={canManage} reason="Only owners and admins can add customers">
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" /> New customer
                </Button>
              </PermissionGuard>
            }
          >
            Add a customer to start sending invoices.
          </EmptyState>
        )
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Customer</TableHead>
                  <TableHead className={TH}>Location</TableHead>
                  <TableHead className={cn(TH, "text-center")}>Address</TableHead>
                  <TableHead className={cn(TH, "text-center")}>Tax number</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Added</TableHead>
                  <TableHead className="w-[48px]" />
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => {
                  const isDeleting = deletingId === customer.id;
                  const link = rowLink(customer.id ? () => onCustomerClick(customer.id!) : undefined);
                  return (
                    <TableRow key={customer.id} {...link}>
                      <TableCell className="max-w-[320px]">
                        <EntityCell
                          name={customer.name}
                          secondary={
                            [customer.email, customer.phone].filter(Boolean).join(" · ") || "No contact details"
                          }
                        />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {[customer.city, customer.country].filter(Boolean).join(", ") || "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        {(() => {
                          const text = addressTooltip(customer);
                          return (
                            <PresenceIndicator
                              present={text.length > 0}
                              tooltip={text}
                              label="Address"
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-center">
                        <PresenceIndicator
                          present={Boolean(customer.taxNumber)}
                          tooltip={customer.taxNumber}
                          label="Tax number"
                        />
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                        {formatDate(customer.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={isDeleting}
                              className="size-8"
                              aria-label={`Actions for ${customer.name ?? "customer"}`}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              {isDeleting ? (
                                <Spinner className="size-4" />
                              ) : (
                                <MoreHorizontal className="size-4" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-40"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <DropdownMenuItem onClick={() => setCustomerToEdit(customer)}>
                              <Pencil className="mr-2 size-4" />
                              Edit
                            </DropdownMenuItem>
                            {canManage && (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setCustomerToDelete(customer)}
                              >
                                <Trash2 className="mr-2 size-4" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>

          <Pager
            page={currentPage}
            hasPrevious={currentPage > 1}
            hasNext={hasMore}
            onPrevious={handlePrevPage}
            onNext={handleNextPage}
          />
        </>
      )}

      <CreateCustomerDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setCursorStack([]);
          setNextCursor(null);
          fetchCustomers();
        }}
      />

      <EditCustomerDialog
        customer={customerToEdit}
        open={customerToEdit !== null}
        onOpenChange={(open) => !open && setCustomerToEdit(null)}
        onUpdated={() => {
          setCustomerToEdit(null);
          fetchCustomers();
        }}
      />

      <AlertDialog
        open={customerToDelete !== null}
        onOpenChange={(open) => !open && setCustomerToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>
              {customerToDelete?.name} will be permanently removed. This will fail if the customer has any associated invoices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
